import { createContext, type PropsWithChildren, useContext, useState } from "react";
import { RemiErrorDeprecated } from "@remi/error";
import type { SuggestedActionPayload } from "@remi/types/socket/message";

interface SuggestedActionContextValue {
  action: SuggestedActionPayload | null;
  setAction: (action: SuggestedActionPayload) => void;
  dismiss: () => void;
}

const SuggestedActionContext = createContext<SuggestedActionContextValue | null>(null);

export const SuggestedActionProvider = ({ children }: PropsWithChildren) => {
  // Latest-only by design — intentionally no queue/stack when actions overlap.
  const [action, setActionState] = useState<SuggestedActionPayload | null>(null);

  const setAction = (incoming: SuggestedActionPayload) => {
    setActionState((current) => {
      // An "ended" live call is a tombstone, not something to show: clear only the matching call
      // and never store it, so a late tombstone from an already-replaced call can't wipe the newer one.
      if (incoming.ended) {
        return current?.phoneCallId === incoming.phoneCallId ? null : current;
      }
      return incoming;
    });
  };

  return (
    <SuggestedActionContext.Provider value={{ action, setAction, dismiss: () => setActionState(null) }}>
      {children}
    </SuggestedActionContext.Provider>
  );
};

export const useSuggestedAction = () => {
  const context = useContext(SuggestedActionContext);
  if (context === null) {
    throw new RemiErrorDeprecated({ message: "useSuggestedAction must be used within a SuggestedActionProvider" });
  }
  return context;
};
